// PATH: src/api/pedagogy.ts — API Phase 2 (pédagogie & vie de classe)
import { useMemo } from "react";
import type { AxiosInstance } from "axios";
import { useApiClient } from "./apiClients";
// Même volet compte que l'inscription d'un élève — un seul type pour les deux.
import type { AccountInput } from "../models/school";

const BASE = "/api/v1/school";

/** Une séance lue dans la grille importée, avec ce qui a pu être résolu. */
export interface TimetableImportSlot {
  sheet: string;
  line: number;
  class_label: string;
  /** 1 = lundi. Sert à ordonner les colonnes de la grille d'aperçu. */
  day_of_week: number;
  day_label: string;
  start_time: string;
  end_time: string;
  raw: string;
  subject_name?: string;
  staff_name?: string;
  room_name?: string;
  /** Libellé lu mais absent du référentiel, par rôle — à associer. */
  missing_subject?: string;
  missing_staff?: string;
  missing_room?: string;
  errors?: string[];
  warnings?: string[];
}

export interface TimetableImportSheet {
  name: string;
  class_label?: string;
  class_id?: number;
  slots: number;
  error?: string;
}

/** Entité du référentiel proposée dans un select de correspondance. */
export interface TimetableRef {
  id: number;
  label: string;
}

/** Associations libellé → id décidées à l'écran, par type d'entité. */
export interface TimetableCorrections {
  subjects?: Record<string, number>;
  staff?: Record<string, number>;
  rooms?: Record<string, number>;
}

export interface TimetableImportPreview {
  sheets: TimetableImportSheet[];
  slots: TimetableImportSlot[];
  /** Classes de l'année, proposées pour rattacher une feuille non reconnue. */
  classes: { id: number; label: string }[];
  /** Libellés distincts absents du référentiel, à associer. */
  unresolved: { subjects: string[]; staff: string[]; rooms: string[] };
  /** Entités existantes proposées dans les selects de correspondance. */
  catalog: { subjects: TimetableRef[]; staff: TimetableRef[]; rooms: TimetableRef[] };
  total: number;
  valid: number;
  invalid: number;
  /** Créneaux déjà saisis pour les classes du fichier — ce que « remplacer » supprimerait. */
  existing_slots: number;
}

export interface TimetableImportResult {
  /** false quand rien n'a été écrit : l'import est tout ou rien. */
  applied: boolean;
  created: number;
  deleted: number;
  failed: number;
  message?: string;
  slots: TimetableImportSlot[];
}

/**
 * Go sérialise une tranche vide en `null`, alors que les types ci-dessus les
 * déclarent comme des tableaux : on rétablit le contrat ici, à la frontière —
 * même parti pris que normalizeProfile dans api/school.ts. Sans cela, un
 * fichier dont aucune feuille n'est rattachée fait planter l'aperçu, qui
 * parcourt ces listes.
 */
const normalizeTimetablePreview = (p: TimetableImportPreview): TimetableImportPreview => ({
  ...p,
  sheets: p.sheets ?? [],
  slots: p.slots ?? [],
  classes: p.classes ?? [],
  unresolved: {
    subjects: p.unresolved?.subjects ?? [],
    staff: p.unresolved?.staff ?? [],
    rooms: p.unresolved?.rooms ?? [],
  },
  catalog: {
    subjects: p.catalog?.subjects ?? [],
    staff: p.catalog?.staff ?? [],
    rooms: p.catalog?.rooms ?? [],
  },
});

/** locked : matière importée du programme marocain — non supprimable. */
export interface Subject { id: number; code: string; name: string; sort_order: number; embed_subject_id?: string; locked?: boolean }
export interface Staff {
  id: number; first_name: string; last_name: string; phone?: string; email?: string; status: string;
  role: string; userapp_id?: number; cin?: string; hire_date?: string; address?: string;
  subject_ids?: number[];
}
export interface Room { id: number; name: string; capacity?: number }
export interface Term { id: number; school_year_id: number; label: string; sort_order: number }
/** locked : lien du programme marocain — coefficient réglable, retrait impossible. */
export interface LevelSubject { subject_id: number; coefficient: number; subject_code?: string; subject_name?: string; locked?: boolean }
export interface TimetableSlot {
  id: number; class_id: number; subject_id: number; staff_id: number; room_id?: number;
  day_of_week: number; start_time: string; end_time: string;
  class_label?: string; subject_name?: string; staff_name?: string; room_name?: string;
}
export interface Assessment {
  id: number; class_id: number; subject_id: number; term_id: number; staff_id?: number; room_id?: number;
  kind: string; title: string; assessment_date?: string; max_score: number; coefficient: number;
  supervisor_ids?: number[]; subject_name?: string; class_label?: string;
}
export interface Score { assessment_id?: number; student_id: number; score?: number | null; is_absent?: boolean; comment?: string; student_name?: string }
export interface Bulletin {
  student_id: number; student_name: string; term_label: string; class_label?: string; level_name?: string;
  overall_average?: number;
  /** Rang dans la classe sur la moyenne générale, et effectif classé. Absents
   *  si l'élève n'a aucune moyenne ou n'est affecté à aucune classe. */
  rank?: number;
  class_size?: number;
  /** Appréciation générale du conseil de classe (migration 0060). */
  general_appreciation?: string;
  subjects: {
    subject_id: number; subject_name: string; coefficient: number; average?: number; assessment_count: number;
    /** Appréciation du professeur sur le trimestre — distincte du commentaire
     *  d'une note ponctuelle (Score.comment). */
    appreciation?: string;
  }[];
}
export interface ProgramItem { id: number; level_id: number; subject_id: number; title: string; sort_order: number; covered: boolean }
export interface TextbookEntry {
  id: number; class_id: number; subject_id: number; staff_id?: number; program_item_id?: number;
  entry_date: string; content: string; subject_name?: string;
}
export interface Homework { id: number; class_id: number; subject_id: number; title: string; description?: string; due_date: string; resource_url?: string; subject_name?: string }
export interface Resource { id: number; class_id?: number; subject_id?: number; title: string; url: string }
export interface AttendanceRecord {
  id: number; student_id: number; class_id?: number; event_date: string; kind: "ABSENCE" | "RETARD";
  minutes_late?: number; justified: boolean; justification?: string;
  notified_at?: string; notification_id?: string; student_name?: string;
  /** Séance de l'emploi du temps visée ; absent = journée entière. */
  slot_id?: number;
  /** Joints (lecture seule) : libellé de la séance rattachée. */
  slot_subject?: string; slot_start?: string; slot_end?: string;
}

/**
 * Motif enregistré quand l'administration justifie elle-même un pointage.
 *
 * Accordé au genre du pointage : « justifié » invariable écrivait « absence
 * justifié ». Cette chaîne est STOCKÉE en base (colonne justification) puis
 * relue telle quelle — les pointages déjà justifiés gardent donc l'ancien
 * libellé, seuls les nouveaux prennent celui-ci.
 */
export const adminJustification = (kind: AttendanceRecord["kind"]): string =>
  kind === "ABSENCE"
    ? "Absence justifiée par l'administration"
    : "Retard justifié par l'administration";

const build = (http: AxiosInstance) => ({
  listSubjects: () => http.get<Subject[]>(`${BASE}/subjects`).then((r) => r.data ?? []),
  createSubject: (v: Partial<Subject>) => http.post(`${BASE}/subjects`, v),
  deleteSubject: (id: number) => http.delete(`${BASE}/subjects/${id}`),

  listStaff: () => http.get<Staff[]>(`${BASE}/staff`).then((r) => r.data ?? []),
  /**
   * account présent → la personne reçoit un compte central tc_identity
   * (type TEACHER, créé INACTIF avec email d'activation). C'est l'appelant qui
   * décide de l'envoyer — le backend n'inspecte pas le rôle.
   */
  createStaff: (v: Partial<Staff> & { account?: AccountInput }) =>
    http.post<Staff & { email_sent: boolean }>(`${BASE}/staff`, v).then((r) => r.data),
  /**
   * Remplace la fiche : le backend écrase phone/email/cin/hire_date/address
   * par ce qui est envoyé (seul `role` est préservé s'il est vide). Toujours
   * partir de la fiche existante et n'en modifier que les champs voulus, sous
   * peine d'effacer le reste.
   */
  updateStaff: (id: number, v: Partial<Staff>) => http.put(`${BASE}/staff/${id}`, v),
  archiveStaff: (id: number) => http.post(`${BASE}/staff/${id}/archive`),
  // Suppression DÉFINITIVE. Le backend refuse (400) tant que des séances ou des
  // évaluations référencent le membre — la fiche doit alors être archivée.
  deleteStaff: (id: number) => http.delete(`${BASE}/staff/${id}`),

  listRooms: () => http.get<Room[]>(`${BASE}/rooms`).then((r) => r.data ?? []),
  createRoom: (v: Partial<Room>) => http.post(`${BASE}/rooms`, v),
  deleteRoom: (id: number) => http.delete(`${BASE}/rooms/${id}`),

  listTerms: (yearId?: number) =>
    http.get<Term[]>(`${BASE}/terms`, { params: { school_year_id: yearId } }).then((r) => r.data ?? []),
  createTerm: (v: Partial<Term>) => http.post(`${BASE}/terms`, v),
  deleteTerm: (id: number) => http.delete(`${BASE}/terms/${id}`),

  getLevelSubjects: (levelId: number) =>
    http.get<LevelSubject[]>(`${BASE}/levels/${levelId}/subjects`).then((r) => r.data ?? []),
  setLevelSubjects: (levelId: number, items: LevelSubject[]) =>
    http.put(`${BASE}/levels/${levelId}/subjects`, items),

  listSlots: (classId?: number, staffId?: number) =>
    http.get<TimetableSlot[]>(`${BASE}/timetable`, { params: { class_id: classId, staff_id: staffId } }).then((r) => r.data ?? []),
  createSlot: (v: Partial<TimetableSlot>) => http.post(`${BASE}/timetable`, v),
  deleteSlot: (id: number) => http.delete(`${BASE}/timetable/${id}`),

  // Import d'emploi du temps au format grille (jours en colonnes). L'année est
  // indispensable : les classes sont identifiées par leur libellé au sein d'une
  // année. classId ne sert que de repli pour un fichier d'une seule feuille.
  // sheetClasses rattache explicitement une feuille à une classe ({"5AEP-A": 12}),
  // quand son nom ne correspond à aucun libellé.
  // corrections associe un libellé non reconnu du fichier à une entité
  // existante : {"staff":{"BENNANI Karim":42},"rooms":{"Salle 3":3}}.
  timetableImportPreview: (
    file: File, schoolYearId: number, classId?: number,
    sheetClasses?: Record<string, number>, corrections?: TimetableCorrections
  ) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("school_year_id", String(schoolYearId));
    if (classId) fd.append("class_id", String(classId));
    if (sheetClasses && Object.keys(sheetClasses).length) fd.append("sheet_classes", JSON.stringify(sheetClasses));
    if (corrections) fd.append("corrections", JSON.stringify(corrections));
    return http
      .post<TimetableImportPreview>(`${BASE}/timetable-import/preview`, fd)
      .then((r) => normalizeTimetablePreview(r.data));
  },
  importTimetable: (
    file: File, schoolYearId: number, replace: boolean, classId?: number,
    sheetClasses?: Record<string, number>, corrections?: TimetableCorrections
  ) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("school_year_id", String(schoolYearId));
    fd.append("replace", replace ? "true" : "false");
    if (classId) fd.append("class_id", String(classId));
    if (sheetClasses && Object.keys(sheetClasses).length) fd.append("sheet_classes", JSON.stringify(sheetClasses));
    if (corrections) fd.append("corrections", JSON.stringify(corrections));
    return http
      .post<TimetableImportResult>(`${BASE}/timetable-import`, fd)
      .then((r) => ({ ...r.data, slots: r.data.slots ?? [] }));
  },
  timetableTemplate: (classId?: number) =>
    http
      .get(`${BASE}/timetable-import/template`, { params: { class_id: classId }, responseType: "blob" })
      .then((r) => r.data as Blob),

  listAssessments: (classId?: number, termId?: number) =>
    http.get<Assessment[]>(`${BASE}/assessments`, { params: { class_id: classId, term_id: termId } }).then((r) => r.data ?? []),
  createAssessment: (v: Partial<Assessment>) => http.post(`${BASE}/assessments`, v),
  deleteAssessment: (id: number) => http.delete(`${BASE}/assessments/${id}`),
  getScores: (assessmentId: number) => http.get<Score[]>(`${BASE}/assessments/${assessmentId}/scores`).then((r) => r.data ?? []),
  putScores: (assessmentId: number, scores: Score[]) => http.put(`${BASE}/assessments/${assessmentId}/scores`, scores),
  getBulletin: (studentId: number, termId: number) =>
    http.get<Bulletin>(`${BASE}/students/${studentId}/bulletin`, { params: { term_id: termId } }).then((r) => r.data),
  /**
   * Appréciations du bulletin. Une valeur vide EFFACE l'appréciation (le
   * backend supprime la ligne plutôt que d'en stocker une vide). Retourne le
   * bulletin réactualisé, inutile de le redemander.
   */
  setAppreciations: (
    studentId: number,
    termId: number,
    v: { general?: string; subjects?: Record<number, string> }
  ) =>
    http
      .put<Bulletin>(`${BASE}/students/${studentId}/appreciations`, { term_id: termId, ...v })
      .then((r) => r.data),

  programProgress: (classId: number, subjectId: number) =>
    http.get<ProgramItem[]>(`${BASE}/classes/${classId}/program`, { params: { subject_id: subjectId } }).then((r) => r.data ?? []),
  createProgramItem: (v: Partial<ProgramItem>) => http.post(`${BASE}/program-items`, v),

  listTextbook: (classId: number, subjectId?: number) =>
    http.get<TextbookEntry[]>(`${BASE}/textbook`, { params: { class_id: classId, subject_id: subjectId } }).then((r) => r.data ?? []),
  createTextbook: (v: Partial<TextbookEntry>) => http.post(`${BASE}/textbook`, v),
  deleteTextbook: (id: number) => http.delete(`${BASE}/textbook/${id}`),

  listHomework: (classId: number) => http.get<Homework[]>(`${BASE}/homework`, { params: { class_id: classId } }).then((r) => r.data ?? []),
  createHomework: (v: Partial<Homework>) => http.post(`${BASE}/homework`, v),
  deleteHomework: (id: number) => http.delete(`${BASE}/homework/${id}`),

  listResources: (classId?: number) => http.get<Resource[]>(`${BASE}/resources`, { params: { class_id: classId } }).then((r) => r.data ?? []),
  createResource: (v: Partial<Resource>) => http.post(`${BASE}/resources`, v),

  recordAttendance: (studentId: number, v: Partial<AttendanceRecord>) =>
    http.post<AttendanceRecord>(`${BASE}/students/${studentId}/attendance`, v).then((r) => r.data),
  // Historique d'assiduité d'un élève (onglet Assiduité de sa fiche).
  listAttendanceByStudent: (studentId: number) =>
    http.get<AttendanceRecord[]>(`${BASE}/students/${studentId}/attendance`).then((r) => r.data ?? []),
  listAttendanceByClass: (classId: number, date: string) =>
    http.get<AttendanceRecord[]>(`${BASE}/attendance`, { params: { class_id: classId, date } }).then((r) => r.data ?? []),
  // Vue mensuelle / registre : tous les pointages d'une classe sur un intervalle.
  listAttendanceByClassRange: (classId: number, from: string, to: string) =>
    http.get<AttendanceRecord[]>(`${BASE}/attendance`, { params: { class_id: classId, from, to } }).then((r) => r.data ?? []),
  justifyAttendance: (id: number, justification: string) => http.post(`${BASE}/attendance/${id}/justify`, { justification }),
});

export type PedagogyApi = ReturnType<typeof build>;
export const usePedagogyApi = (): PedagogyApi => {
  const http = useApiClient("tilmischool");
  return useMemo(() => build(http), [http]);
};
